import { useAxios } from '../../../contexts/AxiosContext'
import { Workspace, Currency, Timezone, VerifyWorkspaceInvite, CreateWorkspaceResponse, WorkspaceInvite, UserWorkspace, Invitation, RemoveWorkspaceResponse, TeamUser, Hostname, Country, WorkspaceCapabilitiesResponse } from '../../../types'

export interface WorkspaceData {
  name: string
  domain: string
  currency: string
}

export type WorkspaceTeamMembersResponse = {
  members: Array<TeamUser>
  invited: Array<Invitation>
}

export type RemoveUserFromWorkspaceResponse = {
  message: string
  status: boolean
}

export type SetUserWorkspaceResponse = {
  workspace_id: string
  user_id: string
}

export type ScriptCompatibilitySettings = {
  osano_enabled: boolean
  edge_mesh_enabled: boolean
}

export const useWorkspaceService = () => {
  const { axiosApiInstance } = useAxios()

  const currencies = async (): Promise<Currency[]> => {
    const response = await axiosApiInstance.get<Currency[]>('/workspace/currencies')
    return response.data
  }

  const countries = async (): Promise<Country[]> => {
    const response = await axiosApiInstance.get<Country[]>('/workspace/countries')
    return response.data
  }

  const timezones = async (): Promise<Timezone[]> => {
    const response = await axiosApiInstance.get<Timezone[]>('/workspace/timezones')
    return response.data
  }

  const getWorkspace = async (workspaceId: string): Promise<Workspace> => {
    const response = await axiosApiInstance.get<Workspace>(`/workspace/workspace/${workspaceId}`)
    return response.data
  }

  const getWorkspaceCapabilities = async (workspaceId: string): Promise<WorkspaceCapabilitiesResponse> => {
    const response = await axiosApiInstance.get<WorkspaceCapabilitiesResponse>(`/workspace/workspace/${workspaceId}/capabilities`)
    return response.data
  }

  const getUserWorkspaces = async (): Promise<UserWorkspace[]> => {
    const response = await axiosApiInstance.get<UserWorkspace[]>('/workspace/user-workspaces')
    return response.data
  }

  const verifyWorkspaceInvite = async (inviteId: string): Promise<VerifyWorkspaceInvite> => {
    const response = await axiosApiInstance.get<VerifyWorkspaceInvite>(`/workspace/verify-workspace-invite/${inviteId}`)
    return response.data
  }

  const createWorkspace = async (name: string, domain: string, currency: string, timezone: string): Promise<CreateWorkspaceResponse> => {
    const response = await axiosApiInstance.post<CreateWorkspaceResponse>('/workspace/create-workspace', { name, domain, currency, timezone })
    return response.data
  }

  const inviteToWorkspace = async (id: string, email: string, role: string, name?: string): Promise<WorkspaceInvite> => {
    const response = await axiosApiInstance.post<WorkspaceInvite>('/workspace/invite-to-workspace', { workspace_id: id, email: email, name: name, role: role })
    return response.data
  }

  const updateWorkspace = async (params: {
    id: string,
    name: string,
    currency: string,
    timezone: string,
    risk_profile?: string,
    mde_sensitivity?: string,
    average_shipping_cost?: number,
    return_policy_window?: number,
    subscription_selling_enabled?: boolean,
    product_feed_enabled?: boolean,
    shopify_url?: string,
    shopify_admin_api_token?: string
  }): Promise<Workspace> => {
    const response = await axiosApiInstance.put<Workspace>(`/workspace/update-workspace/${params.id}`, { ...params })
    return response.data
  }

  const removeWorkspace = async (workspaceId: string): Promise<RemoveWorkspaceResponse> => {
    const response = await axiosApiInstance.delete<RemoveWorkspaceResponse>(`/workspace/delete-workspace/${workspaceId}`)
    return response.data
  }

  const getWorkspaceTeamMembers = async (workspaceId: string): Promise<TeamUser[]> => {
    const response = await axiosApiInstance.get<WorkspaceTeamMembersResponse>(`/workspace/team-workspace/${workspaceId}`)
    return response.data.members
  }

  const removeUserFromWorkspace = async (id: string, email: string): Promise<RemoveUserFromWorkspaceResponse> => {
    const response = await axiosApiInstance.delete<RemoveUserFromWorkspaceResponse>('/workspace/remove-team-workspace', { data: { workspace_id: id, email: email } })
    return response.data
  }

  const getWorkspaceInvites = async (workspaceId: string): Promise<Invitation[]> => {
    const response = await axiosApiInstance.get<Invitation[]>(`/workspace/workspace-invites/${workspaceId}`)
    return response.data
  }

  const cancelInvite = async (id: string, email: string): Promise<{ message: string }> => {
    const response = await axiosApiInstance.delete<{ message: string }>('/workspace/cancel-invite-workspace', { data: { workspace_id: id, email: email } })
    return response.data
  }

  const updateUserRole = async (workspaceId: string, email: string, role: string): Promise<{ message: string }> => {
    const response = await axiosApiInstance.put<{ message: string }>('/workspace/update-team-workspace', { workspace_id: workspaceId, email: email, role: role })
    return response.data
  }

  const domains = async (workspaceId: string): Promise<Hostname[]> => {
    const response = await axiosApiInstance.get<Hostname[]>(`/workspace/workspace-domains/${workspaceId}`)
    return response.data
  }

  const verifyDomain = async (domainId: string): Promise<{ message: string }> => {
    const response = await axiosApiInstance.put<{ message: string }>(`/workspace/verify-domain/${domainId}`)
    return response.data
  }

  const removeDomain = async (domainId: string): Promise<{ message: string }> => {
    const response = await axiosApiInstance.delete<{ message: string }>(`/workspace/remove-domain/${domainId}`)
    return response.data
  }

  const snippetDomain = async (domain: string): Promise<string> => {
    const response = await axiosApiInstance.get<string>(`/workspace/snippet/${domain}`)
    return response.data
  }

  const snippetPixel = async (domain: string): Promise<string> => {
    const response = await axiosApiInstance.get<string>(`/workspace/snippet-pixel/${domain}`)
    return response.data
  }

  const updateScriptCompatibility = async (
    workspaceId: string,
    settings: Partial<ScriptCompatibilitySettings>,
  ): Promise<ScriptCompatibilitySettings> => {
    const response = await axiosApiInstance.put<ScriptCompatibilitySettings>(
      `/workspace/workspace/${workspaceId}/script-compatibility`,
      settings,
    )
    return response.data
  }

  const setUserWorkspace = async (workspaceId: string): Promise<SetUserWorkspaceResponse> => {
    const response = await axiosApiInstance.post<SetUserWorkspaceResponse>(`/workspace/set-user-workspace/${workspaceId}`)
    return response.data
  }

  return {
    currencies,
    timezones,
    countries,
    getWorkspace,
    getWorkspaceCapabilities,
    getUserWorkspaces,
    verifyWorkspaceInvite,
    createWorkspace,
    inviteToWorkspace,
    updateWorkspace,
    removeWorkspace,
    getWorkspaceTeamMembers,
    removeUserFromWorkspace,
    getWorkspaceInvites,
    cancelInvite,
    updateUserRole,
    domains,
    verifyDomain,
    removeDomain,
    snippetDomain,
    snippetPixel,
    updateScriptCompatibility,
    setUserWorkspace
  }
}
